import React, { useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { FaCalendar, FaClipboardList, FaEdit } from 'react-icons/fa';
import API_BASE_URL from '../../../config/api';
import Topbar from '../mini-comp/Topbar';
import Sidebar from '../mini-comp/Sidebar';

const PayrollApplyLeave = () => {
    const [formData, setFormData] = useState({
        start_date: '',
        end_date: '',
        leave_type: '',
        reason: '',
        expected_delivery_date: ''
    });

    const [displayDates, setDisplayDates] = useState({
        start_date: '',
        end_date: '',
        expected_delivery_date: ''
    });

    const [maternityDates, setMaternityDates] = useState({
        earliest: null,
        latest: null
    });

    const [loading, setLoading] = useState(false);

    const showAlert = (type, message) => {
        Swal.fire({
            icon: type,
            title: type === 'success' ? 'Success!' : 'Error!',
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true,
        });
    };

    const formatDateForDisplay = (date) => {
        if (!date) return '';
        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
    };

    const calculateMaternityLeaveDates = (deliveryDate) => {
        if (!deliveryDate) return null;

        const expectedDate = new Date(deliveryDate);

        // Calculate 45 days before
        const earliestDate = new Date(expectedDate);
        earliestDate.setDate(expectedDate.getDate() - 45);

        // Calculate 21 days before
        const latestDate = new Date(expectedDate);
        latestDate.setDate(expectedDate.getDate() - 21);

        return {
            earliest: earliestDate.toISOString().split('T')[0],
            latest: latestDate.toISOString().split('T')[0],
            deliveryDate: expectedDate.toISOString().split('T')[0]
        };
    };

    const calculateEndDate = (startDate) => {
        if (!startDate) return null;
        const start = new Date(startDate);
        const end = new Date(start);
        // Subtract 1 from 98 since we're counting the start date as day 1
        end.setDate(start.getDate() + 97);
        return end.toISOString().split('T')[0];
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setDisplayDates(prev => ({
            ...prev,
            [name]: formatDateForDisplay(value)
        }));

        if (name === 'expected_delivery_date') {
            const dates = calculateMaternityLeaveDates(value);
            if (dates) {
                setMaternityDates(dates);
                setFormData(prev => ({
                    ...prev,
                    start_date: '',
                    end_date: '',
                    expected_delivery_date: value
                }));
                setDisplayDates(prev => ({
                    ...prev,
                    start_date: '',
                    end_date: ''
                }));
            }
        }

        if (formData.leave_type === 'maternity' && name === 'start_date') {
            const endDate = calculateEndDate(value);
            if (endDate) {
                setFormData(prev => ({
                    ...prev,
                    end_date: endDate
                }));
                setDisplayDates(prev => ({
                    ...prev,
                    end_date: formatDateForDisplay(endDate)
                }));
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'leave_type') {
            if (value === 'maternity' || formData.leave_type === 'maternity') {
                setFormData({
                    start_date: '',
                    end_date: '',
                    expected_delivery_date: '',
                    leave_type: value,
                    reason: ''
                });
                setDisplayDates({
                    start_date: '',
                    end_date: '',
                    expected_delivery_date: ''
                });
            } else {
                setFormData(prev => ({
                    ...prev,
                    leave_type: value
                }));
            }
        } else if (name.includes('date')) {
            handleDateChange(e);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validateMaternityDates = () => {
        if (formData.leave_type === 'maternity' && formData.expected_delivery_date) {
            const deliveryDate = new Date(formData.expected_delivery_date);
            const startDate = new Date(formData.start_date);
            const today = new Date();

            if (deliveryDate < today) {
                showAlert('error', 'Expected delivery date cannot be in the past');
                return false;
            }

            const earliestDate = new Date(maternityDates.earliest);
            const latestDate = new Date(maternityDates.latest);

            if (startDate < earliestDate || startDate > latestDate) {
                showAlert('error', 'Start date must be between 45 and 21 days before expected delivery date');
                return false;
            }

            const calculatedEndDate = calculateEndDate(formData.start_date);
            if (formData.end_date !== calculatedEndDate) {
                showAlert('error', 'End date must be exactly 98 days from start date');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.leave_type !== 'maternity') {
            const startDate = new Date(formData.start_date);
            const endDate = new Date(formData.end_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (startDate < today) {
                showAlert('error', 'Start date cannot be in the past');
                return;
            }

            if (endDate < startDate) {
                showAlert('error', 'End date must be after start date');
                return;
            }
        } else {
            if (!validateMaternityDates()) {
                return;
            }
        }

        setLoading(true);

        try {
            const token = Cookies.get('token');

            const formattedData = {
                leave_type: formData.leave_type,
                reason: formData.reason,
                start_date: formData.start_date,
                end_date: formData.end_date,
                status: 'pending',
                ...(formData.leave_type === 'maternity' && {
                    expected_delivery_date: formData.expected_delivery_date
                })
            };

            await axios.post(`${API_BASE_URL}/api/v1/payroll/leaves/`, formattedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            showAlert('success', 'Leave request submitted successfully!');

            setFormData({
                start_date: '',
                end_date: '',
                leave_type: '',
                reason: '',
                expected_delivery_date: ''
            });
            setDisplayDates({
                start_date: '',
                end_date: '',
                expected_delivery_date: ''
            });
        } catch (error) {
            console.error('Error submitting leave request:', error.response?.data || error);
            const errorMessage = error.response?.data?.non_field_errors?.[0] || error.response?.data?.error || 'Failed to submit leave request';
            showAlert('error', errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const todayString = new Date().toISOString().split('T')[0];

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">

                            <div className="col-xl-12 mx-auto">
                                <div className="card">
                                    <div className="card-header">
                                        <h4 className="d-flex align-items-center gap-10 mb-0">
                                            <FaClipboardList className="text-primary" /> Leave Request Form
                                        </h4>
                                    </div>
                                    <br />
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="row gy-4">
                                                <div className="col-12">
                                                    <label className="form-label fw-bold">Leave Type</label>
                                                    <select
                                                        className="form-select"
                                                        name="leave_type"
                                                        value={formData.leave_type}
                                                        onChange={handleChange}
                                                        required
                                                    >
                                                        <option value="">Select Leave Type</option>
                                                        <option value="vacation">Vacation</option>
                                                        <option value="special">Special</option>
                                                        <option value="sick">Sick</option>
                                                        <option value="maternity">Maternity</option>
                                                    </select>
                                                </div>

                                                {formData.leave_type === 'maternity' && (
                                                    <div className="col-12">
                                                        <div className="alert alert-info border-0 shadow-sm d-flex gap-15 align-items-start">
                                                            <FaCalendar className="mt-1 flex-shrink-0" />
                                                            <div>
                                                                <h6 className="fw-bold mb-1">Maternity Leave Guidelines</h6>
                                                                <p className="mb-0 fs-13">
                                                                    Upon entering your expected delivery date, we will calculate your allowed leave window.
                                                                    Maternity leave is strictly 98 days.
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="mb-4">
                                                            <label className="form-label fw-bold">Expected Date of Delivery</label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                name="expected_delivery_date"
                                                                value={formData.expected_delivery_date}
                                                                onChange={handleDateChange}
                                                                required
                                                                min={todayString}
                                                            />
                                                        </div>

                                                        {formData.expected_delivery_date && maternityDates.earliest && (
                                                            <div className="p-3 bg-light-subtle rounded mb-4 border">
                                                                <p className="mb-2 fs-14 text-muted">Based on delivery date ({displayDates.expected_delivery_date}):</p>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span className="fw-bold">Earliest Start:</span>
                                                                    <span>{formatDateForDisplay(maternityDates.earliest)}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between mb-2">
                                                                    <span className="fw-bold">Latest Start:</span>
                                                                    <span>{formatDateForDisplay(maternityDates.latest)}</span>
                                                                </div>
                                                                <small className="text-info d-block mt-2">
                                                                    * Start date must be between 45 and 21 days before delivery.
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold">Start Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="start_date"
                                                        value={formData.start_date}
                                                        onChange={handleChange}
                                                        required
                                                        min={formData.leave_type === 'maternity' ? maternityDates.earliest : todayString}
                                                        max={formData.leave_type === 'maternity' ? maternityDates.latest : undefined}
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold">End Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="end_date"
                                                        value={formData.end_date}
                                                        onChange={handleChange}
                                                        required
                                                        readOnly={formData.leave_type === 'maternity'}
                                                        min={formData.start_date || todayString}
                                                    />
                                                    {formData.leave_type === 'maternity' && <small className="text-muted">Automatically calculated (98 days)</small>}
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-bold d-flex align-items-center gap-8">
                                                        <FaEdit className="text-muted" /> Reason for Leave
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        name="reason"
                                                        value={formData.reason}
                                                        onChange={handleChange}
                                                        rows="4"
                                                        placeholder={formData.leave_type === 'maternity' ? "Additional details..." : "Why are you requesting leave?"}
                                                    ></textarea>
                                                </div>

                                                <div className="col-12 mt-4">
                                                    <button type="submit" className="btn btn-primary w-100 py-2 fs-16 fw-bold" disabled={loading}>
                                                        {loading ? (
                                                            <><span className="spinner-border spinner-border-sm me-2"></span> Submitting...</>
                                                        ) : 'Submit Leave Request'}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayrollApplyLeave;